// src/page/Dashboard/Dashboard.tsx
import React, { useMemo } from "react";
import Card from "../../../components/Card";
import Graph from "../../../components/Graph";
import { faGraduationCap, faBook, faCheck, faTasks } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../../../components/Modal/Modal";
import useDashboard from "../useDashboard";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#1e3a8a", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe", "#dbeafe"];
const DONUT_COLORS = ["#16a34a", "#dc2626", "#f59e0b"];

const Dashboard: React.FC = () => {
  const {
    dashboardDetails,
    isModalOpen,
    reportContent,
    setIsModalOpen,
    submissionSummary,
    navigateToAnalysis
  } = useDashboard();  // Use the custom hook

  const cardData = [
    {
      title: "Total Ebook",
      content: String(dashboardDetails?.totalEBooks),
      icon: <FontAwesomeIcon icon={faBook} />,
    },
    {
      title: "Total Test",
      content: String(dashboardDetails?.totalTest),
      icon: <FontAwesomeIcon icon={faCheck} />,
    },
    {
      title: "All Students",
      content: String(dashboardDetails?.allStudents),
      icon: <FontAwesomeIcon icon={faGraduationCap} />,
    },
  ];

  // Prepare chart data using useMemo
  const chartData = useMemo(() => {
    if (!submissionSummary) return { barData: [], pieData: [], donutData: [] };

    const labels: string[] = submissionSummary.monthlyData.labels || [];
    const counts: number[] = submissionSummary.monthlyData.submissionCounts || [];
    const avgScores: number[] = submissionSummary.monthlyData.avgScores || [];

    const barData = labels.map((label, index) => ({
      month: label.split(" ")[0], // Keep just the month abbreviation for compactness
      submissions: counts[index] || 0,
      avgScore: avgScores[index] !== undefined ? avgScores[index] : 0,
    }));

    const pieData = (submissionSummary.topTestSeries || [])
      .map((t: any) => ({
        name: t.name.length > 25 ? t.name.substring(0, 25) + "…" : t.name,
        value: Number(t.count || t.totalSubmissions) || 0,
      }))
      .filter((d: any) => d.value > 0); // Remove 0-value items that cause Recharts Pie crash

    return { barData, pieData };
  }, [submissionSummary]);

  return (
    <div className="p-4 space-y-6">
      <h1 className="text-2xl text-fontColor font-bold mb-4">Dashboard</h1>
      
      {/* ─── Top Cards Row ──────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cardData.map((data, index) => (
          <Card
            key={index}
            title={data.title}
            content={data.content}
            icon={data.icon}
          />
        ))}
      </div>

      {/* ─── Submission Overview Section ────────── */}
      {submissionSummary && (
        <div className="mt-8 border-t border-gray-100 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-fontColor flex items-center gap-2">
              <FontAwesomeIcon icon={faTasks} className="text-blue-800" />
              Test Submission Overview
            </h2>
            <button
              onClick={navigateToAnalysis}
              className="bg-blue-800 hover:bg-blue-900 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              View Full Analysis &rarr;
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-6 shadow-sm flex flex-col items-center justify-center">
              <div className="text-2xl font-bold text-blue-900">{submissionSummary.totalSubmissions}</div>
              <div className="text-sm font-medium text-gray-500 mt-1">Total Submissions</div>
            </div>
            <div className="bg-green-50 border border-green-100 rounded-xl p-6 shadow-sm flex flex-col items-center justify-center">
              <div className="text-2xl font-bold text-green-900">{submissionSummary.overallStats.avgScore}</div>
              <div className="text-sm font-medium text-gray-500 mt-1">Avg Score</div>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-6 shadow-sm flex flex-col items-center justify-center">
              <div className="text-2xl font-bold text-emerald-900">{submissionSummary.overallStats.avgCorrect}</div>
              <div className="text-sm font-medium text-gray-500 mt-1">Avg Correct</div>
            </div>
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-6 shadow-sm flex flex-col items-center justify-center">
              <div className="text-2xl font-bold text-rose-900">{submissionSummary.overallStats.avgIncorrect}</div>
              <div className="text-sm font-medium text-gray-500 mt-1">Avg Incorrect</div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div 
              className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm group cursor-pointer hover:shadow-md hover:border-blue-200 transition-all relative overflow-hidden"
              onClick={navigateToAnalysis}
            >
              <div className="absolute top-4 right-4 text-xs font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                Click for details &rarr;
              </div>
              <h3 className="font-semibold text-sm text-fontColor mb-4">Monthly Submissions (Last 12 Months)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={chartData.barData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                    cursor={{ fill: "#f8fafc" }}
                  />
                  <Bar dataKey="submissions" name="Submissions" fill="#1e3a8a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div 
              className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm group cursor-pointer hover:shadow-md hover:border-blue-200 transition-all relative overflow-hidden"
              onClick={navigateToAnalysis}
            >
              <div className="absolute top-4 right-4 text-xs font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                Click for details &rarr;
              </div>
              <h3 className="font-semibold text-sm text-fontColor mb-4">Top 5 Tests by Submissions</h3>
              <ResponsiveContainer width="100%" height={260}>
                {chartData.pieData.length === 0 ? (
                  <div className="flex justify-center items-center h-full text-gray-400 text-sm">No data available</div>
                ) : (
                  <PieChart>
                    <Pie
                      data={chartData.pieData}
                      cx="50%"
                      cy="50%"
                      isAnimationActive={true}
                      outerRadius={75}
                      innerRadius={0}
                      paddingAngle={0}
                      dataKey="value"
                      label={({ percent }: { percent?: number }) => `${((percent ?? 0) * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {chartData.pieData.map((_: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="#fff" strokeWidth={2} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "12px", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}
                      wrapperStyle={{ outline: "none" }}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: "11px", paddingTop: "10px" }}
                    />
                  </PieChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* <div className="mt-8">
        <Graph
          data={graphData.datasets[0].data}
          labels={graphData.labels}
          label="Patient Count Summary"
        />
      </div> */}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        content={reportContent || []}
      />
    </div>
  );
};

export default Dashboard;
